import { Component, ChangeDetectionStrategy } from '@angular/core';
import { JsonEditorOptions } from 'ang-jsoneditor';
import { AdminEdi834Service } from '../../services/admin-edi834/admin-edi834.service';

@Component({
    selector: 'app-convert-834',
    templateUrl: './convert-834.component.html',
    styleUrls: ['./convert-834.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class Convert834Component {
  inputPayload = '';
  inputJson: any = null;
  inputIsJson = false;
  forceTextInput = false;

  responsePayload = '';
  responseJson: any = null;
  responseIsJson = false;

  errorMessage = '';
  isLoading = false;

  inputEditorOptions: JsonEditorOptions = this.getJsonEditorOptions(false);
  outputEditorOptions: JsonEditorOptions = this.getJsonEditorOptions(true);

  constructor(private adminEdi834Service: AdminEdi834Service) {}

  async convertToJson(): Promise<void> {
    if (this.inputIsJson) {
      this.errorMessage = 'Input appears to be JSON. Use JSON -> 834 conversion.';
      this.clearResponse();
      return;
    }

    const value = this.inputPayload.trim();
    if (!value) {
      this.errorMessage = 'Input is required.';
      this.clearResponse();
      return;
    }

    this.errorMessage = '';
    this.isLoading = true;

    try {
      const response = await this.adminEdi834Service.convertEdi834ToJson(value);
      this.setResponseValue(this.unwrapPayload(response));
    } catch (error: any) {
      this.errorMessage = this.formatError(error);
      this.clearResponse();
    } finally {
      this.isLoading = false;
    }
  }

  async convertTo834(): Promise<void> {
    if (!this.inputIsJson && !this.inputPayload.trim()) {
      this.errorMessage = 'Input is required.';
      this.clearResponse();
      return;
    }

    let jsonPayload: unknown;
    if (this.inputIsJson) {
      jsonPayload = this.unwrapPayload(this.inputJson);
    } else {
      try {
        jsonPayload = this.unwrapPayload(JSON.parse(this.inputPayload.trim()));
      } catch {
        this.errorMessage = 'Input must be valid JSON for JSON -> 834 conversion.';
        this.clearResponse();
        return;
      }
    }

    this.errorMessage = '';
    this.isLoading = true;

    try {
      const response = await this.adminEdi834Service.convertJsonToEdi834(jsonPayload);
      this.setResponseValue(this.unwrapPayload(response));
    } catch (error: any) {
      this.errorMessage = this.formatError(error);
      this.clearResponse();
    } finally {
      this.isLoading = false;
    }
  }

  onInputTextChanged(value: string): void {
    this.inputPayload = value;
    if (this.forceTextInput) {
      return;
    }
    const parsed = this.tryParseJson(value);
    if (parsed !== null) {
      this.inputJson = parsed;
      this.inputIsJson = true;
    }
  }

  onInputJsonChanged(value: any): void {
    if (value instanceof Event) {
      return;
    }

    this.inputJson = value;
    this.inputPayload = JSON.stringify(value, null, 2);
    this.inputIsJson = true;
    this.forceTextInput = false;
  }

  switchInputToText(): void {
    this.inputIsJson = false;
    this.inputPayload = this.inputJson ? JSON.stringify(this.inputJson, null, 2) : '';
    this.forceTextInput = true;
  }

  switchInputToJson(): void {
    const parsed = this.tryParseJson(this.inputPayload);
    this.inputJson = parsed ?? {};
    this.inputIsJson = true;
    this.forceTextInput = false;
  }

  private formatError(error: any): string {
    if (Array.isArray(error?.data?.Details)) {
      return error.data.Details.join('\n');
    }

    return error?.data?.message || error?.data?.error || error?.error?.message
      || error?.error?.error || error?.message || 'Request failed.';
  }

  private unwrapPayload(value: any): any {
    if (!value || typeof value !== 'object') {
      return value;
    }

    if (value.data !== undefined) {
      return value.data;
    }

    return value;
  }

  private setResponseValue(payload: any): void {
    if (typeof payload === 'string') {
      const parsed = this.tryParseJson(payload);
      if (parsed !== null) {
        this.responseJson = parsed;
        this.responseIsJson = true;
        this.responsePayload = JSON.stringify(parsed, null, 2);
        return;
      }

      this.responsePayload = payload;
      this.responseJson = null;
      this.responseIsJson = false;
      return;
    }

    this.responseJson = payload;
    this.responseIsJson = payload !== null && typeof payload === 'object';
    this.responsePayload = this.responseIsJson ? JSON.stringify(payload, null, 2) : String(payload ?? '');
  }

  private clearResponse(): void {
    this.responsePayload = '';
    this.responseJson = null;
    this.responseIsJson = false;
  }

  private tryParseJson(value: string): any | null {
    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      return null;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  private getJsonEditorOptions(readOnly: boolean): JsonEditorOptions {
    const options = new JsonEditorOptions();
    options.modes = ['tree', 'view', 'text'];
    if (readOnly) {
      options.mode = 'view';
      options.onEditable = () => false;
    } else {
      options.mode = 'tree';
    }
    return options;
  }
}
